import { SkyDialog } from "@skypointcloud/skypoint-ui";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useInvalidateDataflows } from "@/hooks/use-dataflows";
import { useCancelRunById } from "@/hooks/use-runs";

/** #975: why Edit, Disable and Delete are off while the connection's import is queued or running. */
export const NOT_WHILE_IMPORTING = "Not available while an import is running.";

/** Why Save (and "Select all N and save") is off while the import is queued or running: true in both. */
export const SAVE_AFTER_IMPORT = "Save is available when the import finishes.";

export interface CancelImportTarget {
  runId: string;
  dataflowId: string;
  name: string;
}

/**
 * #977: "Cancel the running import for X?", for the Connections row menu and the editor header.
 * It reuses the Runs page's cancel (`useCancelRunById`, audited `run.cancel` on the server), and
 * re-reads the connections list so the status moves to Cancelled without a manual refresh. Stays
 * open with the reason when the cancel is refused.
 */
export function CancelImportDialog({
  target,
  onClose,
}: {
  target: CancelImportTarget | null;
  onClose: () => void;
}) {
  const cancelRun = useCancelRunById();
  const invalidateDataflows = useInvalidateDataflows();
  const notice = useNotice();
  const cancelError = useInlineError();
  const close = () => {
    cancelError.clear();
    onClose();
  };
  return (
    <SkyDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      // Closes on success instead, so a refusal stays on screen with its reason.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="small"
      testId="cancel-import-dialog"
      title="Cancel the import?"
      description={`Cancel the running import for ${target?.name ?? "this connection"}? This stops the whole run, including any other connections it covers. Data already loaded in this run may be incomplete.`}
      primaryAction={{
        label: "Cancel run",
        destructive: true,
        loading: cancelRun.isPending,
        onClick: () => {
          if (!target) return;
          cancelError.clear();
          cancelRun.mutate(target.runId, {
            onSuccess: () => {
              // The list only: the audited detail is never re-read for this.
              invalidateDataflows();
              onClose();
              notice.success(`The run for ${target.name} was cancelled.`);
            },
            onError: (error) => cancelError.show(error, "We could not cancel the import."),
          });
        },
      }}
      secondaryAction={{ label: "Keep running", onClick: close }}
    >
      <InlineErrorBar
        error={cancelError.error}
        title={cancelError.error?.retryable ? undefined : "We could not cancel this import"}
        testId="cancel-import-error"
      />
    </SkyDialog>
  );
}
